/**
 * Legacy fallbacks for catalogs without reasoning metadata. The chat picker
 * prefers thinkingLevelsForModel, which preserves each model's native ladder.
 */

import { THINKING_LEVELS } from './thinking';

/** Canonical column order for matrix UIs. */
const LEVEL_ORDER = [
  'off',
  'minimal',
  'low',
  'standard',
  'high',
  'xhigh',
  'max',
];

/**
 * xAI slugs that still HTTP-200 but are redirect-only (served as grok-4.3).
 * Not listed on `GET https://api.x.ai/v1/models` as of 2026-08-08.
 * FE filters these out of the lasagna even if ada-api still returns them.
 */
export const XAI_REDIRECT_ONLY_IDS = new Set([
  'grok-3',
  'grok-3-mini',
  'grok-4',
]);

/** Groq ids that are decommissioned or scheduled for shutdown (hide from FE). */
export const GROQ_RETIRED_IDS = new Set([
  'mixtral-8x7b-32768',
  'gemma-7b-it',
  'gemma2-9b-it',
  'llama3-8b-8192',
  'llama3-70b-8192',
  // Shutdown 2026-08-16 (free/dev tier) — prefer openai/gpt-oss-* replacements.
  'llama-3.1-8b-instant',
  'llama-3.3-70b-versatile',
]);

/** Levels typically offered per provider when no model-specific override. */
export const PROVIDER_THINKING = {
  auto: [],
  // Ada agent harness — no thinking control (harness owns its loop).
  ada: [],
  // Prefer model-specific anthropicLevels(); last-resort for unknown Claude.
  anthropic: ['off', 'low', 'standard', 'high'],
  // Prefer model-specific xaiLevels(); this is only a last-resort default.
  xai: ['low', 'standard', 'high'],
  groq: ['off', 'standard'],
  openai: ['off'], // non-reasoning default (gpt-4o*); see openaiReasoning()
  // OpenCode Zen: ada's Zen path forwards no thinking params (2026-08) — off only.
  opencode: ['off'],
  // Z.AI: glm-5.3 thinking is always on (cannot send type=disabled). Ada's
  // OpenAICompat path does not yet forward reasoning_effort — hide the control.
  zai: ['off'],
  // MiniMax: thinking.type disabled | adaptive (adaptive is the upstream
  // default). Ada maps off → disabled, low/standard/high → adaptive.
  minimax: ['off', 'standard'],
  // Abliteration: Chat Completions reasoning_effort. Large V2 cannot
  // disable (see thinkingIdsForSelection). Base + previous large can.
  abliteration: ['off', 'low', 'standard', 'high'],
  // Tencent TokenHub: thinking.type enabled|disabled|adaptive on both
  // wires. Ada maps off → disabled, low → small budget, standard/high →
  // large budget. minimax-* upstream is adaptive-only (model-specific
  // override in thinkingIdsForSelection).
  tencent: ['off', 'low', 'standard', 'high'],
  // Moonshot (platform.kimi.ai): Kimi K3 reasons; no documented disable
  // knob on the OpenAI-compat wire — off collapses to upstream default.
  moonshot: ['low', 'standard', 'high'],
  // Alibaba Cloud Model Studio: enable_thinking on/off only; depth is
  // upstream. Same on the anthropic-compat wire (qwen/anthropic/*).
  qwen: ['off', 'standard'],
  // Cerebras Inference: gpt-oss-120b cannot disable (low|medium|high);
  // qwen-3.8-27b accepts none (UI off). Model-specific in thinkingIdsForSelection.
  cerebras: ['low', 'standard', 'high'],
  // Vercel AI Gateway: Chat Completions reasoning_effort. Upstream
  // family decides the ladder in thinkingIdsForSelection.
  vercel: ['off', 'low', 'standard', 'high'],
  // OpenRouter Jev is decisions-only — no thinking control.
  openrouter: [],
  // TypeSafe first-party Jev is decisions-only — no thinking control.
  typesafe: [],
  ollama: ['off'],
  echo: ['off'],
};

/**
 * OpenAI reasoning models (o-series / gpt-5 thinking style) when present in API.
 * Ada currently lists gpt-4o / gpt-4o-mini; keep this ready for when o-* appears.
 */
const OPENAI_REASONING_RE = /^(o[0-9]|gpt-5|computer-use)/i;

function openaiReasoning(modelId) {
  return Boolean(modelId && OPENAI_REASONING_RE.test(modelId));
}

/**
 * Anthropic Claude thinking levels (UI ids). Verified against Ada
 * `GET /models` + platform.claude.com thinking-troubleshooting (2026-08):
 *
 * Ada catalog: haiku-4-5, sonnet-5, opus-5, fable-5, opus-4-8,
 * sonnet-4-6, sonnet-4-5.
 *
 * - Haiku 4.5 / Sonnet 4.5 / Opus 4.5: extended thinking only
 *   (`thinking: {type:"enabled", budget_tokens:N}`); `adaptive` → 400.
 *   Default off. Depth = budget_tokens (min 1024), not `effort`.
 *   UI: off ≡ disabled; low / standard / high ≡ small / medium / large budgets.
 * - Adaptive / effort (Sonnet 4.6, Opus 4.6+, Sonnet 5, Opus 5):
 *   `thinking:{type:"adaptive"}` + `output_config.effort`. UI off ≡ disabled.
 * - Fable 5 / Mythos 5: adaptive always on — no off.
 * - Claude 3.x fluff: no extended-thinking control in Ada → off only.
 * - Unknown claude-*: do not invent levels.
 */
function anthropicLevels(modelId) {
  const id = String(modelId || '').toLowerCase();
  if (!id) return [];
  if (
    id === 'claude-haiku-4-5' ||
    id.startsWith('claude-haiku-4-5-') ||
    id === 'claude-sonnet-4-5' ||
    id.startsWith('claude-sonnet-4-5-') ||
    id === 'claude-opus-4-5' ||
    id.startsWith('claude-opus-4-5-')
  ) {
    return ['off', 'low', 'standard', 'high'];
  }
  // Fable / Mythos: adaptive thinking is always on.
  if (
    /claude-(fable|mythos)-5/.test(id) ||
    id.includes('fable') ||
    id.includes('mythos')
  ) {
    return ['low', 'standard', 'high'];
  }
  // Adaptive / effort models.
  if (
    /claude-(sonnet|opus)-4-6/.test(id) ||
    /claude-(sonnet|opus)-4-[78]/.test(id) ||
    /claude-(sonnet|opus)-5/.test(id)
  ) {
    return ['off', 'low', 'standard', 'high'];
  }
  // Stale Claude 3.x — no Ada extended-thinking wiring.
  if (/^claude-3/.test(id) || id.includes('claude-3-5')) {
    return ['off'];
  }
  if (id.startsWith('claude')) return [];
  return PROVIDER_THINKING.anthropic;
}

/**
 * xAI Grok thinking levels (UI ids). Empirically verified against
 * `https://api.x.ai/v1/chat/completions` + docs.x.ai reasoning page:
 *
 * - grok-4.5: low | medium | high (default high). `none` → 400. UI standard ≡ medium.
 * - grok-4.3: none | low | medium | high. UI off ≡ none, standard ≡ medium.
 * - Redirect-only slugs (grok-3*, grok-4) are filtered from the FE catalog;
 *   if somehow selected, treat like grok-4.3.
 * - Unknown grok-*: do not invent levels.
 */
function xaiLevels(modelId) {
  const id = String(modelId || '').toLowerCase();
  if (!id) return [];
  if (id === 'grok-4.5' || id.startsWith('grok-4.5-')) {
    return ['low', 'standard', 'high'];
  }
  if (
    id === 'grok-4.3' ||
    id.startsWith('grok-4.3-') ||
    XAI_REDIRECT_ONLY_IDS.has(id)
  ) {
    return ['off', 'low', 'standard', 'high'];
  }
  if (id.startsWith('grok')) return [];
  return PROVIDER_THINKING.xai;
}

/** Drop redirect-only / retired xAI ids from API model lists. */
export function filterCatalogModels(models) {
  if (!Array.isArray(models)) return [];
  return models.filter((m) => {
    const id = String(m?.id || '').toLowerCase();
    if (!id) return false;
    // Temporarily keep only the two GLM-backed Ada products in the picker.
    const bare = id.split('/').pop();
    if (['ada', 'ada-harness', 'ada_harness', 'mother-0x-large', 'mother0x-large', 'mother_0x_large'].includes(bare)) return false;
    if (XAI_REDIRECT_ONLY_IDS.has(id)) return false;
    // Also hide bare grok-3* variants if ada-api ever lists them.
    if (/^grok-3($|-)/.test(id)) return false;
    // Hide stale Claude 3.x if a backend ever reintroduces them.
    if (/^claude-3/.test(id) || id.includes('claude-3-5')) return false;
    if (GROQ_RETIRED_IDS.has(id)) return false;
    return true;
  });
}

/**
 * Resolve thinking level ids for the current selection.
 * @param {{ isAuto?: boolean, provider?: string, modelId?: string }} opts
 * @returns {string[]}
 */
export function thinkingIdsForSelection({
  isAuto = false,
  provider = '',
  modelId = '',
} = {}) {
  if (isAuto || !provider || provider === 'auto') return [];
  const p = String(provider).toLowerCase();
  if (p === 'ada') return [];
  if (p === 'openai') {
    return openaiReasoning(modelId)
      ? ['low', 'standard', 'high']
      : PROVIDER_THINKING.openai;
  }
  if (p === 'xai') return xaiLevels(modelId);
  if (p === 'anthropic') return anthropicLevels(modelId);
  if (p === 'abliteration' || p === 'ablation') {
    const id = String(modelId || '').toLowerCase();
    if (id.includes('large-v2')) return ['low', 'standard', 'high'];
    return PROVIDER_THINKING.abliteration;
  }
  if (p === 'tencent') {
    // Compare the bare upstream id (tencent/minimax-m3 → minimax-m3).
    const id = String(modelId || '')
      .toLowerCase()
      .replace(/^.*\//, '');
    if (id.startsWith('minimax')) {
      // minimax-* upstream accepts only adaptive|disabled.
      return ['off', 'standard'];
    }
    return PROVIDER_THINKING.tencent;
  }
  if (p === 'opencode') {
    const bare = String(modelId || '')
      .toLowerCase()
      .replace(/^opencode[/:]/, '');
    if (bare === 'jev' || bare.startsWith('jev-')) {
      return [];
    }
    return PROVIDER_THINKING.opencode;
  }
  if (p === 'cerebras') {
    const id = String(modelId || '')
      .toLowerCase()
      .replace(/^.*\//, '');
    // qwen-3.8-27b: none|low|medium|high (UI off ≡ none).
    if (id.startsWith('qwen-3')) {
      return ['off', 'low', 'standard', 'high'];
    }
    // gpt-oss-* cannot disable; off/none collapse to low on the wire.
    return PROVIDER_THINKING.cerebras;
  }
  if (p === 'vercel') {
    const up = String(modelId || '')
      .toLowerCase()
      .replace(/^vercel[/:]/, '')
      .replace(/^ai-gateway[/:]/, '');
    if (up.startsWith('anthropic/') || up.includes('claude')) {
      return anthropicLevels(up.replace(/^anthropic[/:]/, ''));
    }
    if (up.startsWith('spacexai/') || up.includes('grok')) {
      return xaiLevels(up.replace(/^spacexai[/:]/, ''));
    }
    if (up.startsWith('openai/') && /gpt-5|gpt-6|o[0-9]/.test(up)) {
      return ['low', 'standard', 'high'];
    }
    if (up.startsWith('google/') || up.includes('gemini')) {
      return ['off', 'standard', 'high'];
    }
    if (up.includes('glm')) {
      return PROVIDER_THINKING.zai;
    }
    if (up === 'typesafe-ai/jev' || up === 'jev' || up.endsWith('/jev')) {
      return [];
    }
    return PROVIDER_THINKING.vercel;
  }
  return PROVIDER_THINKING[p] || ['off', 'standard'];
}

/**
 * Full thinking level objects for UI controls.
 * Empty array ⇒ hide thinking UI (Auto, or provider with none).
 */
export function thinkingLevelsForSelection(opts) {
  const ids = thinkingIdsForSelection(opts);
  if (!ids.length) return [];
  return ids
    .map((id) => THINKING_LEVELS.find((t) => t.id === id))
    .filter(Boolean);
}

/**
 * Union of thinking levels across models in a provider focus (lasagna columns).
 * Ordered off → low → standard → high. Empty ⇒ no matrix.
 */
export function thinkingLevelsUnionForModels({
  isAuto = false,
  provider = '',
  models = [],
} = {}) {
  if (isAuto || !provider || provider === 'auto') return [];
  const seen = new Set();
  for (const m of models) {
    for (const id of thinkingIdsForSelection({
      isAuto: false,
      provider: m.provider || provider,
      modelId: m.id,
    })) {
      seen.add(id);
    }
  }
  return LEVEL_ORDER.filter((id) => seen.has(id))
    .map((id) => THINKING_LEVELS.find((t) => t.id === id))
    .filter(Boolean);
}

/** Whether a concrete model supports a thinking level id. */
export function modelSupportsThinkingLevel({
  provider = '',
  modelId = '',
  thinkingId = '',
} = {}) {
  if (!thinkingId) return false;
  return thinkingIdsForSelection({
    isAuto: false,
    provider,
    modelId,
  }).includes(thinkingId);
}

/** Clamp a stored thinking id into the allowed set; prefer standard then first. */
export function clampThinkingId(current, levels, defaultId = 'standard') {
  if (!levels?.length) return current || 'standard';
  if (levels.some((t) => t.id === current)) return current;
  const preferred =
    levels.find((t) => t.id === defaultId) ||
    levels.find((t) => t.id === 'standard') ||
    levels[0];
  return preferred.id;
}

/** Prefer the API's live capability record; older servers use the local map. */
export function thinkingLevelsForModel(model) {
  if (!model || ["image", "video"].includes(model.model_type)) return [];
  if (!model.reasoning)
    return thinkingLevelsForSelection({
      provider: model.provider,
      modelId: model.id,
    });
  const switchOnly =
    !model.reasoning.controls?.includes('effort') &&
    model.reasoning.controls?.includes('thinking');
  const ids = (switchOnly ? [] : model.reasoning.efforts || []).map(
    (effort) => ({ medium: 'standard', none: 'off' })[effort] || effort
  );
  if (model.reasoning.disable_ok) ids.unshift('off');
  // Providers with a thinking switch but no effort ladder have only on/off.
  if (switchOnly) ids.push('standard');
  return THINKING_LEVELS.filter((level) => ids.includes(level.id)).map(
    (level) =>
      switchOnly && level.id === 'standard'
        ? {
            ...level,
            label: 'On',
            hint: 'Let the model choose its thinking depth',
          }
        : level
  );
}

export function thinkingDefaultForModel(model) {
  const value = model?.reasoning?.default;
  return (
    {
      medium: 'standard',
      none: 'off',
      adaptive: 'standard',
      enabled: 'standard',
      disabled: 'off',
    }[value] ||
    value ||
    'standard'
  );
}
